import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Spin,
  Typography,
  Row,
  Col,
  Divider,
  Switch,
  Table,
  Tag,
  Space,
  TimePicker,
} from 'antd';
import {
  RobotOutlined,
  SaveOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const AsistenciaBot = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesLoading, setMensajesLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadConfiguracion();
    loadMensajes();
  }, []);

  const loadConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/asistencia-bot/configuracion');
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          // Convertir hora de formato HH:mm:ss a dayjs
          const horaEnvio = data.cfg_asist_hora_envio 
            ? dayjs(data.cfg_asist_hora_envio, 'HH:mm:ss')
            : dayjs('20:00', 'HH:mm');
          
          form.setFieldsValue({
            activo: data.cfg_asist_activo !== false,
            horaEnvio: horaEnvio,
          });
        }
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar la configuración');
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      message.error('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const loadMensajes = async (page = 1, pageSize = 10) => {
    try {
      setMensajesLoading(true);
      const response = await apiClient.get(
        `/asistencia-bot/mensajes?page=${page}&pageSize=${pageSize}`
      );
      if (response.ok) {
        const data = await response.json();
        setMensajes(data.data || []);
        setPagination({
          current: data.page || 1,
          pageSize: data.pageSize || 10,
          total: data.total || 0,
        });
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los mensajes');
      }
    } catch (error) {
      console.error('Error al cargar mensajes:', error);
      message.error('Error al cargar los mensajes');
    } finally {
      setMensajesLoading(false);
    }
  };

  const handleSave = async (values) => {
    try {
      setSaving(true);
      // Convertir hora de dayjs a formato HH:mm
      const horaEnvio = values.horaEnvio 
        ? values.horaEnvio.format('HH:mm')
        : '20:00';
      
      const payload = {
        activo: values.activo !== false,
        horaEnvio: horaEnvio,
      };
      
      const response = await apiClient.post('/asistencia-bot/configuracion', payload);
      if (response.ok) {
        message.success('Configuración guardada correctamente');
        loadConfiguracion();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      message.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleEnviarManual = async () => {
    try {
      setEnviando(true);
      const response = await apiClient.post('/asistencia-bot/enviar');
      if (response.ok) {
        const data = await response.json();
        message.success(
          `Proceso completado: ${data.data?.mensajesEnviados || 0} mensajes enviados`
        );
        loadMensajes(pagination.current, pagination.pageSize);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al ejecutar el envío');
      }
    } catch (error) {
      console.error('Error al enviar mensajes:', error);
      message.error('Error al ejecutar el envío');
    } finally {
      setEnviando(false);
    }
  };

  const handleTableChange = (newPagination) => {
    loadMensajes(newPagination.current, newPagination.pageSize);
  };

  // Abrir WhatsApp con el número de teléfono
  const handleContactarWhatsApp = (telefono) => {
    if (!telefono) {
      message.warning('No hay número de teléfono disponible');
      return;
    }

    // Limpiar el número: quitar espacios, guiones, paréntesis y el signo +
    const numeroLimpio = telefono.replace(/[\s\-\(\)\+]/g, '');
    
    // Si el número no empieza con código de país (Argentina es 54), asumimos que es local
    // Para Argentina, si empieza con 0, lo quitamos y agregamos 54
    let numeroFormateado = numeroLimpio;
    if (numeroLimpio.startsWith('0')) {
      numeroFormateado = '54' + numeroLimpio.substring(1);
    } else if (!numeroLimpio.startsWith('54') && numeroLimpio.length <= 10) {
      // Si no tiene código de país y parece ser un número local argentino, agregar 54
      numeroFormateado = '54' + numeroLimpio;
    }

    // Abrir WhatsApp
    const url = `https://wa.me/${numeroFormateado}`;
    window.open(url, '_blank');
  };

  const getRespuestaTag = (respuesta) => {
    if (!respuesta) {
      return <Tag icon={<ClockCircleOutlined />} color="default">Pendiente</Tag>;
    }
    if (respuesta === 'SI') {
      return <Tag icon={<CheckCircleOutlined />} color="success">Sí</Tag>;
    }
    return <Tag icon={<CloseCircleOutlined />} color="error">No</Tag>;
  };

  const columns = [
    {
      title: 'Fecha Turno',
      dataIndex: 'msg_asist_fecha_turno',
      key: 'fecha_turno',
      render: (text) => {
        const fecha = dayjs(text);
        return fecha.format('DD/MM/YYYY');
      },
    },
    {
      title: 'Alumno',
      key: 'alumno',
      render: (record) => `${record.alu_nombre} ${record.alu_apellido}`,
    },
    {
      title: 'Teléfono',
      key: 'telefono',
      render: (record) => (
        <Space>
          <span>{record.alu_telefono || '-'}</span>
          {record.alu_telefono && (
            <Button
              type="link"
              icon={<MessageOutlined />}
              onClick={() => handleContactarWhatsApp(record.alu_telefono)}
              style={{ 
                padding: 0, 
                height: 'auto',
                color: '#25D366'
              }}
            >
              Contactar
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'Turno',
      key: 'turno',
      render: (record) => {
        const hora = record.tur_hora_desde || 'N/A';
        const cancha = record.cancha_descripcion || 'N/A';
        return `${hora} - ${cancha}`;
      },
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_descripcion',
      key: 'categoria',
    },
    {
      title: 'Enviado',
      dataIndex: 'msg_asist_fecha_envio',
      key: 'fecha_envio',
      render: (text) => {
        if (!text) return '-';
        const fecha = dayjs(text);
        return fecha.format('DD/MM/YYYY HH:mm');
      },
    },
    {
      title: 'Respuesta',
      dataIndex: 'msg_asist_respuesta',
      key: 'respuesta',
      render: (respuesta) => getRespuestaTag(respuesta),
    },
    {
      title: 'Fecha Respuesta',
      dataIndex: 'msg_asist_fecha_respuesta',
      key: 'fecha_respuesta',
      render: (text) => {
        if (!text) return '-';
        const fecha = dayjs(text);
        return fecha.format('DD/MM/YYYY HH:mm');
      },
    },
  ];

  if (loading) {
    return (
      <MainLayout>
        <div style={{ 
          padding: '50px', 
          textAlign: 'center',
          minHeight: 'calc(100vh - 64px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <Title level={isMobile ? 4 : 2} style={{ marginBottom: '24px', color: '#e0e0e0' }}>
          Asistencia Bot
        </Title>

        <Row gutter={[24, 24]}>
          <Col xs={24} lg={16}>
            <Card
              title={
                <span>
                  <RobotOutlined style={{ marginRight: 8 }} />
                  Configuración de Envío Automático
                </span>
              }
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadConfiguracion}
                  size="small"
                >
                  Recargar
                </Button>
              }
            >
              <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                Configura el envío automático de mensajes de asistencia a los alumnos.
                El sistema enviará mensajes a la hora configurada para los turnos del día siguiente.
              </Text>

              <Divider />

              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                autoComplete="off"
              >
                <Form.Item
                  name="activo"
                  label={
                    <span>
                      <RobotOutlined style={{ marginRight: 8 }} />
                      Envío Automático Activado
                    </span>
                  }
                  valuePropName="checked"
                  style={{ marginBottom: '32px' }}
                >
                  <Switch 
                    checkedChildren="Activado" 
                    unCheckedChildren="Desactivado"
                    size="default"
                  />
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px', marginTop: '-16px' }}>
                  Cuando está activado, el sistema enviará automáticamente mensajes de asistencia
                  a la hora configurada para los turnos del día siguiente.
                </Text>

                <Divider />

                <Form.Item
                  name="horaEnvio"
                  label="Hora de Envío"
                  rules={[
                    { required: true, message: 'Selecciona la hora de envío' },
                  ]}
                >
                  <TimePicker
                    format="HH:mm"
                    style={{ width: '100%' }}
                    size="large"
                  />
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: '24px', marginTop: '-16px' }}>
                  Hora a la que se enviarán los mensajes de asistencia (formato 24 horas).
                  Ejemplo: 20:00 para enviar a las 8:00 PM.
                </Text>

                <Divider />

                <Form.Item style={{ marginBottom: 0 }}>
                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={saving}
                      size="large"
                    >
                      Guardar Configuración
                    </Button>
                    <Button
                      type="default"
                      icon={<SendOutlined />}
                      onClick={handleEnviarManual}
                      loading={enviando}
                      size="large"
                    >
                      Enviar Ahora (Prueba)
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Card title="Información">
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                <strong>¿Cómo funciona?</strong>
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                El sistema enviará automáticamente mensajes de WhatsApp a los alumnos
                que tienen turnos programados para el día siguiente.
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                Los alumnos recibirán un mensaje con los datos del turno y dos botones:
                <strong> Sí</strong> o <strong>No</strong> para confirmar su asistencia.
              </Text>
              <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
                La asistencia se actualizará automáticamente según la respuesta del alumno.
              </Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
          <Col xs={24}>
            <Card
              title="Mensajes Enviados"
              extra={
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => loadMensajes(pagination.current, pagination.pageSize)}
                  size="small"
                  loading={mensajesLoading}
                >
                  Recargar
                </Button>
              }
            >
              <Table
                columns={columns}
                dataSource={mensajes}
                rowKey="msg_asist_id"
                loading={mensajesLoading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          </Col>
        </Row>
      </div>
    </MainLayout>
  );
};

export default AsistenciaBot;

